import { Link, useRouter } from "@shared/client/navigation";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  Gift,
  LoaderCircle,
  Mails,
  PenLine,
  Play,
  Trash2,
  TriangleAlert,
  UserRoundSearch,
  CircleCheck,
} from "lucide-react";
import type { ProspectDetail, ProspectLeadRecord, ProspectStatus } from "@shared/lib/prospects-shared";
import { isResearchStale, isUnopened, PROSPECT_STATUS_LABELS } from "@shared/lib/prospects-shared";
import {
  effectiveClass,
  MESSAGE_STATUS_LABELS,
  RANK_LABELS,
  REPLY_CLASS_LABELS,
  type MessageRecord,
  type ReplyRecord,
} from "@shared/lib/prospection/shared";
import { formatDate, formatDateTime } from "@shared/lib/format";
import { AdminChip, AdminMeta, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { ContactPicker, ContactProfileCard, type ContactView } from "@shared/components/contact";
import { OrgLinks } from "@shared/components/org-links";
import { OrgLogo } from "@shared/components/org-logo";
import { absoluteUrl, hostOf } from "@shared/lib/source-links";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Switch } from "@shared/components/ui/switch";
import { Textarea } from "@shared/components/ui/textarea";
import { cn } from "@shared/lib/utils";

/**
 * Fiche d'un prospect de la base de prospection : ce qu'il vend et à qui, les
 * signaux que l'agent a trouvés pour lui, et la séquence d'e-mails qui les lui
 * livre depuis les boîtes Gmail, réponses comprises. La relecture détaillée
 * se fait dans l'onglet Envois.
 */

async function api(path: string, method: string, body?: unknown) {
  const response = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data as { error?: string }).error || "Opération impossible.");
  return data;
}

/** L'agent date en ISO quand il le peut, en texte libre sinon. */
function formatObserved(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatDate(value) : value;
}

/** Trois paliers de maturité, du plus chaud au plus froid. */
const READINESS_TONE: Record<string, string> = {
  "Demande active": "danger",
  "Besoin explicite": "warning",
  "Fenêtre favorable": "neutral",
};

export function ProspectWorkspace({ prospect }: { prospect: ProspectDetail }) {
  const router = useRouter();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const stale = isResearchStale(prospect);
  const running = prospect.research_status === "running" && !stale;

  // L'analyse tourne côté serveur après la réponse HTTP : on rafraîchit la
  // fiche tant qu'elle n'est pas terminée.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => router.refresh(), 5000);
    return () => clearInterval(timer);
  }, [running, router]);

  // Un dossier ouvert n'est plus « nouveau » dans /admin/prospection, jusqu'à la prochaine analyse.
  const unopened = isUnopened(prospect);
  useEffect(() => {
    if (unopened) void fetch(`/api/admin/prospection/${prospect.id}/opened`, { method: "POST" }).catch(() => {});
  }, [unopened, prospect.id]);

  async function act(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setError("");
    try {
      await action();
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Opération impossible.");
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="grid gap-4.5">
      <AdminMeta
        note={
          <span className="flex flex-wrap gap-2">
            <Button
              disabled={running || busy === "research" || !prospect.website}
              onClick={() => act("research", () => api(`/api/admin/prospection/${prospect.id}/research`, "POST"))}
              size="sm"
              title={prospect.website ? undefined : "Renseignez d’abord le site du prospect."}
              type="button"
              variant="outline"
            >
              {running || busy === "research" ? <LoaderCircle className="animate-spin" /> : <Play />}
              {prospect.researched_at ? "Relancer l’analyse" : "Analyser et trouver des signaux"}
            </Button>
            <Button
              className="text-muted-foreground hover:text-destructive"
              disabled={busy === "delete"}
              onClick={() => {
                if (!confirm(`Supprimer ${prospect.name} de la base de prospection ?`)) return;
                act("delete", async () => {
                  await api(`/api/admin/prospection/${prospect.id}`, "DELETE");
                  router.push("/admin/prospection");
                });
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <Trash2 /> Supprimer
            </Button>
          </span>
        }
      >
        <span className="flex flex-wrap items-center gap-2.5">
          <Button asChild className="h-auto px-0 text-muted-foreground" size="sm" variant="link">
            <Link href="/admin/prospection"><ArrowLeft className="size-3.5" /> Prospection</Link>
          </Button>
          <OrgLogo name={prospect.name} size={28} website={prospect.website} />
          <strong className="text-[13.5px] font-semibold text-foreground">{prospect.name}</strong>
          {/* Les mêmes liens que la fiche d'un lead : site, LinkedIn, Pappers, plan. */}
          <OrgLinks organization={prospect} />
          <Select
            onValueChange={(value) =>
              act("status", () =>
                api(`/api/admin/prospection/${prospect.id}`, "PATCH", { status: value as ProspectStatus }),
              )
            }
            value={prospect.status}
          >
            <SelectTrigger aria-label="Statut du prospect" className="h-8 text-xs font-semibold" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PROSPECT_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </span>
      </AdminMeta>

      {error && (
        <Alert role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {running && (
        <Alert className="bg-muted text-foreground">
          <LoaderCircle className="animate-spin" />
          <AlertDescription className="text-primary">
            L’agent {prospect.contact_name ? "" : "cherche la personne à contacter, "}étudie leur offre et leur clientèle,
            cherche des signaux à leur livrer, puis rédige le premier e-mail.
            Comptez quelques minutes ; cette page se met à jour toute seule.
          </AlertDescription>
        </Alert>
      )}
      {stale && (
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertDescription>
            L’analyse lancée le {formatDateTime(prospect.research_started_at)} ne s’est jamais terminée. Relancez-la.
          </AlertDescription>
        </Alert>
      )}
      {prospect.research_status === "failed" && prospect.research_error && (
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertDescription>Dernière analyse en échec : {prospect.research_error}</AlertDescription>
        </Alert>
      )}

      <ContactPanel busy={busy} onAction={act} prospect={prospect} />
      {/* Remonté à chaque mise à jour : l'agent peut avoir rempli l'interlocuteur. */}
      <IdentityPanel busy={busy} key={prospect.updated_at} onAction={act} prospect={prospect} />
      <OfferPanel busy={busy} onAction={act} prospect={prospect} />
      <LeadsPanel busy={busy} onAction={act} prospect={prospect} />
      <SequencePanel busy={busy} onAction={act} prospect={prospect} />
    </div>
  );
}

export type ActionRunner = (key: string, action: () => Promise<unknown>) => Promise<void>;

/** Ce que montre la fiche d'un interlocuteur saisi à la main : la même carte que pour un membre du comité. */
function manualContact(prospect: ProspectDetail): ContactView | null {
  if (!prospect.contact_name) return null;
  return {
    id: "retenu",
    contact_name: prospect.contact_name,
    contact_role: prospect.contact_role,
    decision_role: null,
    confidence: null,
    contact_linkedin_url: prospect.contact_linkedin_url,
    contact_photo_url: prospect.contact_photo_url,
    public_professional_email: prospect.contact_email,
    contact_reason: prospect.contact_reason,
  };
}

/**
 * La personne à qui vendre Hune, montrée comme l'app montre les interlocuteurs
 * d'un lead (shared/components/contact.tsx) : le comité d'approche trouvé par
 * l'agent à gauche, la fiche de la personne sélectionnée à droite, avec de
 * quoi la retenir tant qu'aucun e-mail n'est parti.
 */
export function ContactPanel({
  prospect,
  onAction,
  busy,
}: {
  prospect: ProspectDetail;
  onAction: ActionRunner;
  busy: string;
}) {
  const committee = prospect.contact_committee ?? [];
  const isRetained = (member: { contact_name: string }) => member.contact_name === prospect.contact_name;
  const retained = committee.find(isRetained) ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(retained?.id ?? committee[0]?.id ?? null);
  const locked = prospect.sequence_step > 0;
  const origin =
    prospect.contact_origin === "agent"
      ? `Trouvé par l’agent${prospect.contact_researched_at ? ` le ${formatDate(prospect.contact_researched_at)}` : ""}`
      : prospect.contact_origin === "manual"
        ? "Saisi à la main"
        : null;
  const action = origin ? <span className="text-[12px] text-muted-foreground">{origin}</span> : undefined;

  // Un interlocuteur saisi à la main (ou un comité vide) : sa seule fiche.
  const manual = manualContact(prospect);
  if (committee.length === 0 || (manual && !retained)) {
    return (
      <AdminPanel action={action} title="Interlocuteur">
        {manual ? (
          <ContactProfileCard contact={manual} eyebrow="Interlocuteur" organization={prospect} />
        ) : prospect.contact_researched_at ? (
          <p className="m-0 text-[13px] text-muted-foreground">
            L’agent n’a trouvé personne de vérifiable le {formatDateTime(prospect.contact_researched_at)}. Saisissez
            l’interlocuteur dans la fiche : sans lui, le premier e-mail commence par « Bonjour, ».
          </p>
        ) : (
          <p className="m-0 flex items-center gap-2 text-[13px] text-muted-foreground">
            <UserRoundSearch className="size-4" /> L’agent cherchera la personne à contacter à la prochaine analyse.
          </p>
        )}
      </AdminPanel>
    );
  }

  const selected = committee.find((member) => member.id === selectedId) ?? retained ?? committee[0];
  const selectedIsRetained = isRetained(selected);
  const contactFor = (member: (typeof committee)[number]): ContactView =>
    isRetained(member)
      ? {
          ...member,
          // Un profil collé à la main (écran « à vérifier ») complète ce qu'avait trouvé l'agent.
          contact_linkedin_url: prospect.contact_linkedin_url ?? member.contact_linkedin_url,
          public_professional_email: prospect.contact_email,
        }
      : member;

  return (
    <AdminPanel action={action} title="Interlocuteur">
      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <section aria-label="Comité d’approche" className="grid content-start gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="m-0 text-[14px] font-semibold">Comité d’approche</h3>
            <small className="text-[12px] text-muted-foreground">
              {committee.length} profil{committee.length > 1 ? "s" : ""}
            </small>
          </div>
          <ContactPicker
            contacts={committee}
            marker={(member) =>
              isRetained(member) ? (
                <CircleCheck aria-label="Interlocuteur retenu" className="size-3.5 flex-none text-muted-foreground" />
              ) : null
            }
            onSelect={setSelectedId}
            selectedId={selected.id}
          />
        </section>
        <ContactProfileCard
          actions={
            selectedIsRetained ? (
              <span className="flex-none text-[12px] font-medium text-muted-foreground">Interlocuteur retenu</span>
            ) : locked ? (
              <span className="flex-none text-[12px] text-muted-foreground">Séquence commencée</span>
            ) : (
              <Button
                className="flex-none"
                disabled={busy === "contact"}
                onClick={() =>
                  onAction("contact", () =>
                    api(`/api/admin/prospection/${prospect.id}/contact`, "POST", { memberId: selected.id }),
                  )
                }
                size="sm"
                type="button"
                variant="outline"
              >
                {busy === "contact" ? <LoaderCircle className="animate-spin" /> : <Check />} Retenir
              </Button>
            )
          }
          contact={contactFor(selected)}
          organization={prospect}
        >
          {selected.sources.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
              {selected.sources.map((url) => (
                <a className="inline-flex items-center gap-1 text-primary hover:underline" href={absoluteUrl(url)} key={url} rel="noreferrer noopener" target="_blank">
                  {hostOf(url) ?? url} <ExternalLink className="size-3" />
                </a>
              ))}
            </div>
          )}
          {!selectedIsRetained && !locked && (
            <p className="m-0 text-[12px] text-muted-foreground">
              Retenir cette personne garde le premier e-mail en attente : seule la formule d’appel change.
            </p>
          )}
        </ContactProfileCard>
      </div>
    </AdminPanel>
  );
}

function IdentityPanel({
  prospect,
  onAction,
  busy,
}: {
  prospect: ProspectDetail;
  onAction: ActionRunner;
  busy: string;
}) {
  const [name, setName] = useState(prospect.name);
  const [website, setWebsite] = useState(prospect.website ?? "");
  const [contactName, setContactName] = useState(prospect.contact_name ?? "");
  const [contactRole, setContactRole] = useState(prospect.contact_role ?? "");
  const [contactEmail, setContactEmail] = useState(prospect.contact_email ?? "");
  const [linkedin, setLinkedin] = useState(prospect.contact_linkedin_url ?? "");
  const [source, setSource] = useState(prospect.source ?? "");
  const [notes, setNotes] = useState(prospect.notes ?? "");
  const [autoEnabled, setAutoEnabled] = useState(prospect.auto_enabled);

  const field = (id: string) => `prospect-${id}`;

  return (
    <AdminPanel title="Fiche">
      <form
        className="grid gap-4 md:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          onAction("identity", () =>
            api(`/api/admin/prospection/${prospect.id}`, "PATCH", {
              name,
              website: website || null,
              contact_name: contactName || null,
              contact_role: contactRole || null,
              contact_email: contactEmail || null,
              contact_linkedin_url: linkedin || null,
              source: source || null,
              notes: notes || null,
              auto_enabled: autoEnabled,
            }),
          );
        }}
      >
        <div className="grid gap-2">
          <Label htmlFor={field("name")}>Entreprise</Label>
          <Input id={field("name")} onChange={(event) => setName(event.target.value)} required value={name} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={field("website")}>Site web</Label>
          <Input id={field("website")} inputMode="url" onChange={(event) => setWebsite(event.target.value)} placeholder="https://" value={website} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={field("contact")}>Interlocuteur</Label>
          <Input id={field("contact")} onChange={(event) => setContactName(event.target.value)} value={contactName} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={field("role")}>Fonction</Label>
          <Input id={field("role")} onChange={(event) => setContactRole(event.target.value)} value={contactRole} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={field("email")}>E-mail</Label>
          <Input id={field("email")} onChange={(event) => setContactEmail(event.target.value)} type="email" value={contactEmail} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={field("linkedin")}>LinkedIn</Label>
          <Input id={field("linkedin")} inputMode="url" onChange={(event) => setLinkedin(event.target.value)} value={linkedin} />
        </div>
        <div className="grid gap-2 md:col-span-2">
          <Label htmlFor={field("source")}>Origine</Label>
          <Input id={field("source")} onChange={(event) => setSource(event.target.value)} placeholder="Où l’avez-vous trouvé ?" value={source} />
        </div>
        <div className="grid gap-2 md:col-span-2">
          <Label htmlFor={field("notes")}>Notes internes</Label>
          <Textarea id={field("notes")} onChange={(event) => setNotes(event.target.value)} rows={2} value={notes} />
        </div>
        <div className="flex items-center gap-3 md:col-span-2">
          <Switch checked={autoEnabled} id={field("auto")} onCheckedChange={setAutoEnabled} />
          <Label htmlFor={field("auto")}>Inclure dans la file de préparation automatique</Label>
        </div>
        <div className="md:col-span-2">
          <Button disabled={busy === "identity"} type="submit">
            {busy === "identity" ? <LoaderCircle className="animate-spin" /> : <Check />} Enregistrer
          </Button>
        </div>
      </form>
      <p className="mt-4 mb-0 text-[12px] leading-relaxed text-muted-foreground">{describeQueueState(prospect)}</p>
    </AdminPanel>
  );
}

/** Où en est ce prospect vis-à-vis de la préparation automatique. */
function describeQueueState(prospect: ProspectDetail) {
  const history =
    prospect.touch_count === 0
      ? "Jamais préparé par la machine."
      : `Préparé ${prospect.touch_count} fois par la machine, la dernière le ${formatDateTime(prospect.last_touch_at)}.`;

  if (prospect.status === "unsubscribed") {
    return `${history} Désinscrit${prospect.unsubscribed_at ? ` le ${formatDate(prospect.unsubscribed_at)}` : ""} : son adresse est exclue pour toujours.`;
  }
  if (prospect.status !== "new") return `${history} Il ne repassera pas en préparation automatique.`;
  if (!prospect.auto_enabled) return `${history} Hors file automatique : il ne sera repris qu’à la main.`;
  if (prospect.next_eligible_at && new Date(prospect.next_eligible_at) > new Date()) {
    return `${history} Protégé jusqu’au ${formatDate(prospect.next_eligible_at)} : ni lui, ni une autre fiche portant le même contact ne seront repris avant cette date.`;
  }
  return `${history} Éligible au prochain passage.`;
}

export function OfferPanel({
  prospect,
  onAction,
  busy,
}: {
  prospect: ProspectDetail;
  onAction: ActionRunner;
  busy: string;
}) {
  const [editing, setEditing] = useState(false);
  const [offerName, setOfferName] = useState(prospect.offer_name ?? "");
  const [offerSummary, setOfferSummary] = useState(prospect.offer_summary ?? "");
  const [segment, setSegment] = useState(prospect.priority_segment ?? "");
  const [decisionMaker, setDecisionMaker] = useState(prospect.decision_maker ?? "");
  const [favorableEvent, setFavorableEvent] = useState(prospect.favorable_event ?? "");

  const analyzed = Boolean(prospect.offer_name || prospect.offer_summary);

  return (
    <AdminPanel
      action={
        analyzed && (
          <Button onClick={() => setEditing((value) => !value)} size="sm" type="button" variant="outline">
            <PenLine /> {editing ? "Annuler" : "Corriger"}
          </Button>
        )
      }
      title="Ce qu’ils vendent"
    >
      {!analyzed ? (
        <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">
          Lancez l’analyse : l’agent lit leur site, reconstitue leur offre et leur client type, puis part chercher un
          prospect pour eux.
        </p>
      ) : editing ? (
        <form
          className="grid gap-4 md:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            onAction("offer", async () => {
              await api(`/api/admin/prospection/${prospect.id}`, "PATCH", {
                offer_name: offerName || null,
                offer_summary: offerSummary || null,
                priority_segment: segment || null,
                decision_maker: decisionMaker || null,
                favorable_event: favorableEvent || null,
              });
              setEditing(false);
            });
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="offer-name">Offre</Label>
            <Input id="offer-name" onChange={(event) => setOfferName(event.target.value)} value={offerName} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="offer-decision">Décideur type</Label>
            <Input id="offer-decision" onChange={(event) => setDecisionMaker(event.target.value)} value={decisionMaker} />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="offer-summary">Proposition de valeur</Label>
            <Textarea id="offer-summary" onChange={(event) => setOfferSummary(event.target.value)} rows={3} value={offerSummary} />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="offer-segment">Clients qu’ils visent</Label>
            <Textarea id="offer-segment" onChange={(event) => setSegment(event.target.value)} rows={2} value={segment} />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label htmlFor="offer-event">Événement qui rend le besoin urgent</Label>
            <Textarea id="offer-event" onChange={(event) => setFavorableEvent(event.target.value)} rows={2} value={favorableEvent} />
          </div>
          <div className="md:col-span-2">
            <Button disabled={busy === "offer"} type="submit">
              {busy === "offer" ? <LoaderCircle className="animate-spin" /> : <Check />} Enregistrer
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <dl className="m-0 grid gap-3.5 sm:grid-cols-2">
            {[
              { term: "Offre", value: prospect.offer_name },
              { term: "Proposition de valeur", value: prospect.offer_summary },
              { term: "Clients qu’ils visent", value: prospect.priority_segment },
              { term: "Décideur type", value: prospect.decision_maker },
              { term: "Événement déclencheur", value: prospect.favorable_event },
            ].map((item) => (
              <div key={item.term}>
                <dt className="text-[12px] font-medium text-muted-foreground">
                  {item.term}
                </dt>
                <dd className="m-0 mt-1.5 text-[13px] leading-relaxed">{item.value ?? "—"}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 mb-0 text-[12px] text-muted-foreground">
            {prospect.offer_edited_at
              ? `Corrigé à la main le ${formatDate(prospect.offer_edited_at)} : l’agent ne l’écrase plus.`
              : `Compris par l’agent le ${formatDate(prospect.researched_at)}.`}
          </p>
        </div>
      )}
    </AdminPanel>
  );
}

export function LeadsPanel({
  prospect,
  onAction,
  busy,
}: {
  prospect: ProspectDetail;
  onAction: ActionRunner;
  busy: string;
}) {
  const firstSent = prospect.messages.some(
    (message) => message.kind === "sequence" && message.rank === 1 && ["sending", "sent"].includes(message.status),
  );
  // Rang de la séquence qui livre chaque signal.
  const deliveredBy = new Map<string, MessageRecord>();
  for (const message of prospect.messages) {
    if (message.kind === "sequence" && message.lead_id && message.status !== "cancelled") deliveredBy.set(message.lead_id, message);
  }

  return (
    <AdminPanel title={`Signaux trouvés pour eux (${prospect.leads.length})`}>
      {prospect.leads.length === 0 ? (
        <p className="m-0 flex items-start gap-2 text-[12.5px] leading-relaxed text-muted-foreground">
          <Gift aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          Aucun signal pour l’instant. L’analyse en produit jusqu’à cinq, du plus prometteur au moins prometteur : le
          premier part dans le premier e-mail, le deuxième dans une relance, les autres dans la réponse.
        </p>
      ) : (
        <ul className="m-0 grid list-none gap-3 p-0">
          {prospect.leads.map((lead) => (
            <LeadCard
              busy={busy}
              deliveredBy={deliveredBy.get(lead.id) ?? null}
              firstSent={firstSent}
              key={lead.id}
              lead={lead}
              onAction={onAction}
              prospectId={prospect.id}
            />
          ))}
        </ul>
      )}
    </AdminPanel>
  );
}

function LeadCard({
  prospectId,
  lead,
  deliveredBy,
  firstSent,
  onAction,
  busy,
}: {
  prospectId: string;
  lead: ProspectLeadRecord;
  deliveredBy: MessageRecord | null;
  firstSent: boolean;
  onAction: ActionRunner;
  busy: string;
}) {
  const key = `email-${lead.id}`;
  const isFirst = deliveredBy?.rank === 1;
  return (
    <li className={cn("rounded-lg border p-4", deliveredBy && "border-primary/35 bg-secondary/50")}>
      <div className="flex flex-wrap items-center gap-2.5">
        <strong className="text-[14px] font-semibold tracking-tight">{lead.organization}</strong>
        {lead.readiness && (
          <AdminChip tone={READINESS_TONE[lead.readiness] ?? "neutral"}>{lead.readiness}</AdminChip>
        )}
        <span className="text-[12px] font-semibold tabular-nums">{lead.priority ?? "—"}/100</span>
        {deliveredBy && (
          <AdminChip tone="active">
            {RANK_LABELS[deliveredBy.rank ?? 1]} · {MESSAGE_STATUS_LABELS[deliveredBy.status].toLowerCase()}
          </AdminChip>
        )}
      </div>

      {lead.market_clue && <p className="mt-2.5 mb-0 text-[13px] leading-relaxed">{lead.market_clue}</p>}
      {lead.relevance && (
        <p className="mt-1.5 mb-0 text-[12.5px] leading-relaxed text-muted-foreground">{lead.relevance}</p>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-3 text-[12px] text-muted-foreground">
        {lead.source_link ? (
          <a
            className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
            href={lead.source_link}
            rel="noreferrer noopener"
            target="_blank"
          >
            {lead.source_name || "Source"} <ExternalLink className="size-3" />
          </a>
        ) : (
          <span>Sans source citable : ne peut pas être livré</span>
        )}
        {lead.observed_on && <span>Observé le {formatObserved(lead.observed_on)}</span>}
        {lead.category && <span>{lead.category}</span>}
      </div>

      {!firstSent && lead.source_link && (
        <div className="mt-3.5 flex flex-wrap gap-2">
          <Button
            disabled={busy === key}
            onClick={() => onAction(key, () => api(`/api/admin/prospection/${prospectId}/email`, "POST", { leadId: lead.id }))}
            size="sm"
            type="button"
            variant={isFirst ? "outline" : "default"}
          >
            {busy === key ? <LoaderCircle className="animate-spin" /> : <Mails />}
            {isFirst ? "Réécrire le premier e-mail" : "Livrer ce signal dans le premier e-mail"}
          </Button>
        </div>
      )}
    </li>
  );
}

type TimelineEntry = { at: string; message?: MessageRecord; reply?: ReplyRecord };

function SequencePanel({
  prospect,
  onAction,
  busy,
}: {
  prospect: ProspectDetail;
  onAction: ActionRunner;
  busy: string;
}) {
  const [copied, setCopied] = useState("");
  const entries: TimelineEntry[] = [
    ...prospect.messages
      .filter((message) => message.status !== "cancelled")
      .map((message) => ({ at: message.sent_at ?? message.created_at, message })),
    ...prospect.replies.map((reply) => ({ at: reply.received_at, reply })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  const cancelled = prospect.messages.filter((message) => message.status === "cancelled").length;

  return (
    <AdminPanel
      action={
        <span className="text-[12px] text-muted-foreground">
          {prospect.sequence_step > 0
            ? `${prospect.sequence_step} e-mail(s) envoyé(s)${prospect.next_step_on ? ` · relance due le ${formatDate(prospect.next_step_on)}` : ""}`
            : prospect.contact_email
              ? `à ${prospect.contact_email}`
              : "adresse du destinataire manquante"}
        </span>
      }
      title="Séquence"
    >
      {entries.length === 0 ? (
        <p className="m-0 flex items-start gap-2 text-[12.5px] leading-relaxed text-muted-foreground">
          <Mails aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          Aucun e-mail pour l’instant. Le premier livre le meilleur signal, sans lien, et pose une seule question ; la
          source part dans la réponse.
        </p>
      ) : (
        <ol className="m-0 grid list-none gap-3 p-0">
          {entries.map((entry) =>
            entry.reply ? (
              <li className="rounded-lg border border-dashed p-4" key={`reply-${entry.reply.id}`}>
                <div className="flex flex-wrap items-center gap-2.5 text-[12.5px]">
                  <strong className="text-[13px] font-semibold">Réponse du prospect</strong>
                  <AdminChip tone="info">{REPLY_CLASS_LABELS[effectiveClass(entry.reply)]}</AdminChip>
                  <span className="text-muted-foreground">{formatDateTime(entry.reply.received_at)}</span>
                </div>
                <p className="mt-2 mb-0 text-[13px] leading-relaxed whitespace-pre-wrap">{entry.reply.excerpt || "(message vide)"}</p>
              </li>
            ) : entry.message ? (
              <MessageEntry busy={busy} copied={copied} key={entry.message.id} message={entry.message} onAction={onAction} onCopied={setCopied} />
            ) : null,
          )}
        </ol>
      )}
      <p className="mt-4 mb-0 text-[12px] leading-relaxed text-muted-foreground">
        La signature s’ajoute à l’envoi.
        {cancelled > 0 ? ` ${cancelled} brouillon(s) remplacé(s) ou annulé(s) ne sont pas affichés.` : ""} Envoyé à la main ?
        Passez le statut à « Contacté » : la séquence automatique s’arrête.
      </p>
    </AdminPanel>
  );
}

function MessageEntry({
  message,
  busy,
  copied,
  onAction,
  onCopied,
}: {
  message: MessageRecord;
  busy: string;
  copied: string;
  onAction: ActionRunner;
  onCopied: (id: string) => void;
}) {
  const tab =
    message.status === "held" ? "held" : message.status === "failed" ? "failed" : message.status === "ready" ? "ready" : "to_review";
  return (
    <li className="rounded-lg border p-4">
      <div className="flex flex-wrap items-center gap-2.5 text-[12.5px]">
        <strong className="text-[13px] font-semibold">
          {message.kind === "reply" ? "Notre réponse" : RANK_LABELS[message.rank ?? 1]}
        </strong>
        <AdminChip tone={message.status === "sent" ? "active" : message.status === "held" || message.status === "failed" ? "danger" : "trial"}>
          {MESSAGE_STATUS_LABELS[message.status]}
        </AdminChip>
        <span className="text-muted-foreground">
          {message.sent_at ? `envoyé le ${formatDateTime(message.sent_at)}` : `préparé le ${formatDateTime(message.created_at)}`}
        </span>
      </div>
      {message.subject && <p className="mt-2 mb-0 text-[13px] font-medium">{message.subject}</p>}
      <p className="mt-1.5 mb-0 text-[13px] leading-relaxed whitespace-pre-wrap">{message.body}</p>
      {(message.hold_reason && message.status === "held") || message.error ? (
        <p className="mt-2 mb-0 text-[12.5px] text-destructive">{message.error ?? message.hold_reason}</p>
      ) : null}
      {["to_review", "held", "ready", "failed"].includes(message.status) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(message.status === "to_review" || message.status === "held") && (
            <Button
              disabled={busy === `approve-${message.id}`}
              onClick={() =>
                onAction(`approve-${message.id}`, () =>
                  api(`/api/admin/prospection/messages/${message.id}`, "PATCH", { action: "approve" }),
                )
              }
              size="sm"
              type="button"
            >
              {busy === `approve-${message.id}` ? <LoaderCircle className="animate-spin" /> : <Check />} Valider
            </Button>
          )}
          <Button asChild size="sm" variant="outline">
            <Link href={`/admin/prospection/envois?tab=${tab}`}>
              <PenLine /> Relire dans Envois
            </Link>
          </Button>
          {message.kind === "sequence" && message.rank === 1 && (
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(`${message.subject ?? ""}\n\n${message.body}`);
                onCopied(message.id);
              }}
              size="sm"
              title="Pour l’envoyer à la main, hors des boîtes Gmail"
              type="button"
              variant="ghost"
            >
              <Copy /> {copied === message.id ? "Copié" : "Copier"}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
